import { contentChildren, Directive, effect, inject } from '@angular/core';
import { HxHeaderCell } from '../header-cell';
import { HxTableComponent } from '../table';

/**
 * Table header container directive
 *
 * @example
 *   ```html
 *   <hx-table>
 *     <hx-table-header>
 *       <!-- Pixel width (number) -->
 *       <hx-header-cell [width]="200">Name</hx-header-cell>
 *
 *       <!-- Percentage width -->
 *       <hx-header-cell [width]="'25%'">Status</hx-header-cell>
 *
 *       <!-- Flexible fr units -->
 *       <hx-header-cell [width]="'2fr'">Description</hx-header-cell>
 *     </hx-table-header>
 *   </hx-table>
 *   ```;
 */
@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: 'hx-table-header',
  standalone: true,
  host: {
    role: 'row',
  },
})
export class HxTableHeaderDirective {
  readonly headerCells = contentChildren(HxHeaderCell);

  private tableComponent = inject(HxTableComponent);

  constructor() {
    effect(() => this.setGridColumns());
  }

  private setGridColumns(): void {
    // Width handling:
    // - Pixel widths (px): Use exact pixel values (fixed column width)
    // - Percentage widths (%): Used directly; if ALL columns use % and total < 100,
    //   the remaining space is distributed equally across all columns.
    // - Fr units (fr): Use directly
    // - No width: Use 1fr for equal distribution
    const cells = this.headerCells();
    const rawWidths = cells.map((cell) => cell.getCssWidth());

    // Check if every column has an explicit percentage width
    const allPercent = rawWidths.every((w) => typeof w === 'string' && w.endsWith('%'));

    let finalWidths: string[];

    if (allPercent) {
      const percentValues = rawWidths.map((w) => parseFloat(w!));
      const total = percentValues.reduce((sum, v) => sum + v, 0);

      if (total < 100) {
        // Distribute remaining space equally across all columns
        const extra = (100 - total) / cells.length;
        finalWidths = percentValues.map((v) => `${v + extra}%`);
      } else {
        // Total is 100% or more — use as-is
        finalWidths = rawWidths as string[];
      }
    } else {
      finalWidths = rawWidths.map((w) => {
        if (!w) return '1fr';
        return w;
      });
    }

    this.tableComponent.updateGridColumns(finalWidths.join(' '));
  }
}
